import React from "react";
import get from "lodash/get";
import assign from "lodash/assign";
import { Link } from "react-router";
import {
  Tabs,
  Tab,
  CircularProgress,
  RaisedButton,
  FlatButton,
  TextField,
  Checkbox
} from "material-ui";

import { snakeCase } from "../util";

import InfoNotice from "./partials/info-notice.jsx";
import BackButton from "./partials/back-button.jsx";
import WorkingIndicator from "./partials/working-indicator.jsx";

import CurrencyField from "./partials/currency-field.jsx";
import BasicHtmlField from "./partials/basic-html-field.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import ItemBucketActions from "../actions/ItemBucketActions";
import ItemBucketStore from "../stores/ItemBucketStore";
import EventBucketStore from "../stores/EventBucketStore";

class ItemBucketEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      itemBucket: null,
      dirty: false,
      isSaving: false,
      lastUpdatedAt: false,
      values: {}
    };

    this.onChangeValue = this.onChangeValue.bind(this);
    this.save = this.save.bind(this);
    this.delete = this.delete.bind(this);
  }

  static getStores() {
    return [ItemBucketStore, EventBucketStore];
  }

  static getPropsFromStores() {
    return {
      getItemBucketById: ItemBucketStore.getItemBucketById,
      getEventBucketById: EventBucketStore.getEventBucketById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  getStyles() {
    return {
      actions: {
        marginTop: 20
      },
      editor: {
        marginTop: 60,
        paddingTop: 40,
        borderTop: "1px solid rgb(224, 224, 224)"
      }
    };
  }

  onChangeValue(id, value) {
    this.state.dirty = true;
    this.state.values[id] = value;
    this.setState(this.state);
  }

  save() {
    var values = assign({}, this.state.values);
    this.setState({
      isSaving: true,
      lastUpdatedAt: this.state.itemBucket.updated_at
    });
    ItemBucketActions.edit.defer(this.props.params.itemBucketId, values);
  }

  delete() {
    let bucket = this.state.itemBucket.event_bucket;
    if (
      window.confirm(
        "Are you sure? This will delete all variants & all purchased items."
      )
    ) {
      ItemBucketActions.delete(this.props.params.itemBucketId);
      this.context.router.push(`/event_buckets/${bucket}/item_buckets`);
    }
  }

  componentDidMount() {
    this.context.router.setRouteLeaveHook(this.props.route, () => {
      if (this.state.dirty) {
        return "Are you sure you wish to leave the item bucket editor? Your changes will not be saved.";
      }
    });

    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(newProps) {
    var itemBucket = newProps.getItemBucketById(newProps.params.itemBucketId);

    if (!this.state.isFetchingItemBucket && !itemBucket) {
      ItemBucketActions.fetchOne(newProps.params.itemBucketId);
      this.setState({ isFetchingItemBucket: true });
    } else if (itemBucket) {
      this.setState({ isFetchingItemBucket: false });
    }

    if (
      Object.keys(this.state.values).length === 0 ||
      this.state.itemBucket.id !== newProps.params.itemBucketId ||
      (this.state.isSaving &&
        itemBucket.updated_at !== this.state.lastUpdatedAt)
    ) {
      this.setState({
        itemBucket,
        dirty: false,
        isSaving: false,
        values: itemBucket
          ? {
              name: itemBucket.name || "",
              variants: (itemBucket.variants.length
                ? itemBucket.variants
                : [{ name: "Default", unlimited: true }]
              ).map(v => ({
                id: v.id,
                name: v.name,
                price: v.price,
                variable_price: v.variable_price,
                variable_price_presets: v.variable_price_presets,
                variable_price_allow_custom: v.variable_price_allow_custom,
                quantity: v.quantity,
                unlimited: v.unlimited,
                active: v.active
              }))
            }
          : {}
      });
    }
  }

  _makeCoreEditor() {
    let styles = this.getStyles();

    if (this.state.itemBucket && this.state.itemBucket.code === "donation") {
      return (
        <div style={{ marginTop: 15 }}>
          You cannot edit the donation item bucket; it is created automatically
          by the system.
          <br />
          (You may enable/disable donations via{" "}
          <Link
            to={`/event_buckets/${this.state.itemBucket.event_bucket}/settings`}
          >
            Settings
          </Link>
          .)
        </div>
      );
    }

    if (this.state.itemBucket && this.state.itemBucket.code === "service_fee") {
      return (
        <div style={{ marginTop: 15 }}>
          You cannot edit the box office service fee item bucket; it is created
          automatically by the system.
          <br />
          (You may enable/disable the box office service fee via{" "}
          <Link
            to={`/event_buckets/${this.state.itemBucket.event_bucket}/settings`}
          >
            Settings
          </Link>
          .)
        </div>
      );
    }
    return (
      <div>
        <p style={{ marginBottom: 0 }}>
          <InfoNotice style={{ marginTop: 5 }}>
            Check out our{" "}
            <a
              href="https://help.eventive.org/item-sales-and-donations/create-item-buckets"
              target="_blank"
            >
              guide to creating item buckets
            </a>
            .
          </InfoNotice>
        </p>
        <div>
          <div>
            <TextField
              value={get(this.state, "values.name", "")}
              floatingLabelText="Name"
              onChange={e => {
                this.onChangeValue("name", e.target.value);
                // No codes pls
                // if (!this.state.hasEditedCode) {
                //   // Automatically generate a url code if they haven't entered one
                //   this.onChangeValue('code', snakeCase(e.target.value));
                // }
              }}
            />
          </div>
        </div>
        <h2 style={{ marginTop: "1em" }}>Variants</h2>
        <div>
          <div style={{ marginTop: 5 }}>
            Specify one or more variants of the item bucket (e.g. sizes).
          </div>
          <div style={{ marginTop: 5 }}>
            {this.state.values.variants.map((variant, i) => {
              return (
                <div
                  key={i}
                  style={{
                    border: "1px solid #ccc",
                    padding: "0 10px 10px",
                    position: "relative",
                    marginBottom: 10
                  }}
                >
                  <TextField
                    value={variant.name}
                    floatingLabelText="Variant name"
                    floatingLabelFixed={true}
                    onChange={e => {
                      this.state.values.variants[i].name = e.target.value;
                      this.onChangeValue(
                        "variants",
                        this.state.values.variants
                      );
                    }}
                  />
                  {!variant.variable_price_allow_custom ? (
                    <CurrencyField
                      value={variant.price}
                      floatingLabelText="Price"
                      floatingLabelFixed={true}
                      placeholder="$0.00"
                      style={{ marginLeft: 10, width: 100 }}
                      onChange={e => {
                        this.state.values.variants[i].price = e.target.value;
                        this.onChangeValue(
                          "variants",
                          this.state.values.variants
                        );
                      }}
                    />
                  ) : null}
                  <Checkbox
                    label="Variable price (prompt custom entry)"
                    checked={variant.variable_price_allow_custom || false}
                    style={{ marginTop: "0.5em", display: "block", width: 300 }}
                    onCheck={(e, checked) => {
                      this.state.values.variants[i].variable_price = checked;
                      this.state.values.variants[
                        i
                      ].variable_price_allow_custom = checked;
                      this.onChangeValue(
                        "variants",
                        this.state.values.variants
                      );
                    }}
                  />
                  <Checkbox
                    label="Unlimited quantity"
                    checked={variant.unlimited || false}
                    style={{ marginTop: "0.5em", display: "block", width: 200 }}
                    onCheck={(e, checked) => {
                      this.state.values.variants[
                        i
                      ].unlimited = !variant.unlimited;
                      this.onChangeValue(
                        "variants",
                        this.state.values.variants
                      );
                    }}
                  />
                  {!variant.unlimited ? (
                    <TextField
                      value={variant.quantity}
                      floatingLabelText="Quantity"
                      floatingLabelFixed={true}
                      type="number"
                      style={{ width: 75, display: "block" }}
                      onChange={e => {
                        this.state.values.variants[i].quantity = e.target.value;
                        this.onChangeValue(
                          "variants",
                          this.state.values.variants
                        );
                      }}
                    />
                  ) : null}
                  {i !== 0 && (
                    <div style={{ position: "absolute", top: 0, right: 0 }}>
                      <FlatButton
                        style={{ minWidth: 10 }}
                        label="×"
                        onTouchTap={() => {
                          this.state.values.variants.splice(i, 1);
                          this.onChangeValue(
                            "variants",
                            this.state.values.variants
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <RaisedButton
            label="Add another variant"
            onTouchTap={() => {
              this.onChangeValue(
                "variants",
                this.state.values.variants.concat([{ unlimited: true }])
              );
            }}
          />
        </div>
      </div>
    );
  }

  _makeVariantsEditor() {
    let styles = this.getStyles();

    return <div></div>;
  }

  render() {
    let styles = this.getStyles();

    if (!this.state.itemBucket) {
      return <WorkingIndicator fullPage={true} />;
    }

    return (
      <div>
        <BackButton
          label={
            this.props.location.state && this.props.location.state.fromListing
              ? "item buckets"
              : "item bucket"
          }
          style={{ marginRight: 7 }}
          onTouchTap={() =>
            this.props.location.state && this.props.location.state.fromListing
              ? this.context.router.goBack()
              : this.context.router.push(
                  `/event_buckets/${this.props.params.eventBucketId}/item_buckets/${this.props.params.itemBucketId}`
                )
          }
        />
        {this._makeCoreEditor()}
        {this._makeVariantsEditor()}
        <div style={styles.actions}>
          <RaisedButton
            label={this.state.dirty ? "Save changes" : "No changes to save"}
            primary={true}
            disabled={!this.state.dirty}
            onTouchTap={this.save}
          />
          <RaisedButton
            label="Delete item bucket"
            onTouchTap={this.delete}
            disabled={
              this.state.itemBucket &&
              ["donation", "service_fee"].indexOf(
                this.state.itemBucket.code
              ) !== -1
            }
            style={{ marginLeft: 7 }}
          />
        </div>
      </div>
    );
  }
}

export default connectToStores(ItemBucketEditor);



// WEBPACK FOOTER //
// ./src/app/components/item-bucket-editor.jsx